import type { Attribute } from '@strapi/strapi';
import { CaretRight } from '@phosphor-icons/react';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ButtonText from '@tmh-website/ui/ButtonText';
import CMSLink from '@tmh-website/components/shared/CMSLink';
import LocaleSelect from '@tmh-website/components/global/LocaleSelect';
import SocialLinks, { type SocialLinksProps } from '@tmh-website/components/global/SocialLinks';
import StyledGlobal from '@tmh-website/styles/global';
import theme from '@tmh-website/styles/theme';
import type { LocalePrefix } from '@tmh-website/shared/const';
import type { LocaleSelectProps } from '@tmh-website/components/global/LocaleSelect';
import { Color } from '@tmh-website/shared/colors';
import { filterFooterLinksByAudience, filterFooterLogosByAudience } from '@tmh-website/services/cms.service';
import { GlobalContext, ProjectContext } from '@tmh-website/shared';
import { hasLocales } from '@tmh-website/shared/const';
import { isStrapiImage, isValidLinkField } from '@tmh-website/shared/cms';

import * as Styled from './Footer.styles';
import FooterLegalLinks from './FooterLegalLinks';
import FooterLinkGroup from './FooterLinkGroup';
import FooterPartnersBlock from './FooterPartnersBlock';
import FooterSignup from './FooterSignup';
import type { FooterProps as StyledFooterProps } from './Footer.styles';

export interface FooterProps {
  /**
   * @see StyledFooterProps.$backgroundColor
   */
  backgroundColor?: StyledFooterProps['$backgroundColor'];

  /**
   * Enable this flag when this component is used outside of the Next.js context.
   */
  isStandaloneMode?: boolean;

  locale: LocalePrefix;
}

const Footer = ({ backgroundColor, isStandaloneMode, locale }: FooterProps) => {
  const {
    audience,
    footerCertificates,
    footerContactSectionHeadline,
    footerContactSectionLink,
    footerContactSectionText,
    footerLegalLinks,
    footerLinkGroups,
    footerLogisticPartners,
    footerPartners,
    footerPaymentPartners,
    footerSpecialLink,
    footerTopLogo,
    socialLinks,
  } = useContext(GlobalContext);
  const { t } = useTranslation();
  const { project } = useContext(ProjectContext);

  if (!footerLinkGroups || !footerLegalLinks) {
    return null;
  }

  /**
   * Get visible navigation entries
   */

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const visibleFooterLinkGroups = useMemo<Attribute.GetValues<'navigation.footer-link-group'>[]>(
    () => filterFooterLinksByAudience(footerLinkGroups, audience, locale, project),
    [footerLinkGroups, audience, locale]
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const visibleFooterPartnersLogos = useMemo(
    () => filterFooterLogosByAudience(footerPartners, audience, locale, project),
    [footerPartners, audience, locale]
  );
  const showFooterPartners = footerPartners && visibleFooterPartnersLogos.length > 0;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const visibleFooterPaymentPartnersLogos = useMemo(
    () => filterFooterLogosByAudience(footerPaymentPartners, audience, locale, project),
    [footerPaymentPartners, audience, locale]
  );
  const showFooterPaymentPartners = footerPaymentPartners && visibleFooterPaymentPartnersLogos.length > 0;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const visibleFooterLogisticPartnersLogos = useMemo(
    () => filterFooterLogosByAudience(footerLogisticPartners, audience, locale, project),
    [footerLogisticPartners, audience, locale]
  );
  const showFooterLogisticPartners = footerLogisticPartners && visibleFooterLogisticPartnersLogos.length > 0;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const visibleFooterCertificates = useMemo(
    () => filterFooterLogosByAudience(footerCertificates, audience, locale, project),
    [footerCertificates, audience, locale]
  );
  const showFooterCertificates = footerCertificates && visibleFooterCertificates.length > 0;

  const linkGroupColumns: Attribute.GetValues<'navigation.footer-link-group'>[][] = [];

  visibleFooterLinkGroups.forEach(linkGroup => {
    const colId = parseInt(linkGroup.columnNumber) - 1;
    !!linkGroupColumns[colId] ? linkGroupColumns[colId].push(linkGroup) : (linkGroupColumns[colId] = [linkGroup]);
  });

  const socialLinksThemeColor: SocialLinksProps['themeColor'] =
    backgroundColor === Color.Green_1000_Energy ? Color.Green_1000_Energy : Color.Blue_1000_Deep;

  const localeSelectThemeColor: LocaleSelectProps['themeColor'] =
    backgroundColor === Color.Green_1000_Energy ? Color.Green_1000_Energy : Color.Blue_1000_Deep;

  return (
    <Styled.Footer $backgroundColor={backgroundColor}>
      <StyledGlobal.Wrapper>
        {/* Top logo (optional) */}
        {isStrapiImage(footerTopLogo) && (
          <Styled.TopLogoWrapper>
            <Styled.TopLogo
              alt={footerTopLogo.alternativeText || ''}
              height={footerTopLogo.height}
              src={footerTopLogo.url}
              width={footerTopLogo.width}
            />
          </Styled.TopLogoWrapper>
        )}
        {/* END | Top logo (optional) */}

        {linkGroupColumns.length > 0 && (
          <>
            <Styled.TopRow as='nav' aria-label={t('global:navigation.footer.site') as string}>
              {linkGroupColumns.map((column, index) => (
                <Styled.SiteLinksCol key={index}>
                  {column.map(linkGroup => (
                    <FooterLinkGroup
                      key={linkGroup.id}
                      linkGroup={linkGroup}
                      isStandaloneMode={!!isStandaloneMode}
                      locale={locale}
                    />
                  ))}
                </Styled.SiteLinksCol>
              ))}
            </Styled.TopRow>

            <Styled.Divider />
          </>
        )}

        <Styled.BottomRow>
          {(showFooterPaymentPartners || showFooterCertificates) && (
            <Styled.BottomCol $width={225}>
              {showFooterPaymentPartners && (
                <FooterPartnersBlock
                  isStandaloneMode={!!isStandaloneMode}
                  title={footerPaymentPartners.title}
                  text={footerPaymentPartners.text}
                  entries={visibleFooterPaymentPartnersLogos}
                  locale={locale}
                />
              )}
              {showFooterCertificates && (
                <FooterPartnersBlock
                  isStandaloneMode={!!isStandaloneMode}
                  title={footerCertificates.title}
                  text={footerCertificates.text}
                  entries={visibleFooterCertificates}
                  locale={locale}
                  isCertificates
                />
              )}
            </Styled.BottomCol>
          )}

          {(showFooterPartners || showFooterLogisticPartners) && (
            <Styled.BottomCol $width={174}>
              {showFooterPartners && (
                <FooterPartnersBlock
                  isStandaloneMode={!!isStandaloneMode}
                  title={footerPartners.title}
                  text={footerPartners.text}
                  entries={visibleFooterPartnersLogos}
                  locale={locale}
                />
              )}
              {showFooterLogisticPartners && (
                <FooterPartnersBlock
                  isStandaloneMode={!!isStandaloneMode}
                  title={footerLogisticPartners.title}
                  text={footerLogisticPartners.text}
                  entries={visibleFooterLogisticPartnersLogos}
                  locale={locale}
                />
              )}
            </Styled.BottomCol>
          )}

          <Styled.BottomCol>
            <FooterSignup
              locale={locale}
              isStandaloneMode={!!isStandaloneMode}
              project={project}
              title={footerContactSectionHeadline}
              text={footerContactSectionText}
              link={footerContactSectionLink}
            />
            <SocialLinks
              socialLinks={socialLinks}
              themeColor={socialLinksThemeColor}
              iconOpacity={0.6}
              centeredAt={theme.breakpoint.md.up}
            />
          </Styled.BottomCol>

          <Styled.BottomCol $desktopAlign='right' $colMargin={30}>
            {footerSpecialLink && isValidLinkField(footerSpecialLink) && (
              <CMSLink link={footerSpecialLink} isStandaloneMode={isStandaloneMode} locale={locale}>
                <ButtonText
                  iconRight={<CaretRight weight='bold' />}
                  transparentHover
                  color={Color.Blue_500_Ultramarine}>
                  {footerSpecialLink.label}
                </ButtonText>
              </CMSLink>
            )}

            {hasLocales(project) && (locale as string) !== 'default' && (
              <>
                <br />
                <LocaleSelect locale={locale} themeColor={localeSelectThemeColor} />
              </>
            )}
          </Styled.BottomCol>
        </Styled.BottomRow>

        <FooterLegalLinks locale={locale} isStandaloneMode={!!isStandaloneMode} />
      </StyledGlobal.Wrapper>
    </Styled.Footer>
  );
};

export default Footer;
